import { el, escape } from './dom';
import { BLOG_POSTS } from '../blog/posts';

export type FooterLink = { href: string; label: string };

export type FooterSocialLink = {
  href: string;
  label: string;
  /** Inline SVG markup. Caller is responsible for sanitising. */
  svg: string;
};

const BLUESKY_SVG =
  '<svg viewBox="0 0 64 57" aria-hidden="true" fill="currentColor"><path d="M13.873 3.805C21.21 9.332 29.103 20.537 32 26.55v15.882c0-.338-.13.044-.41.867-1.512 4.456-7.418 21.847-20.923 7.944-7.111-7.32-3.819-14.64 9.125-16.85-7.405 1.264-15.73-.825-18.014-9.015C1.12 23 0 8.51 0 6.55 0-3.268 8.579-.182 13.873 3.805ZM50.127 3.805C42.79 9.332 34.897 20.537 32 26.55v15.882c0-.338.13.044.41.867 1.512 4.456 7.418 21.847 20.923 7.944 7.111-7.32 3.819-14.64-9.125-16.85 7.405 1.264 15.73-.825 18.014-9.015C62.88 23 64 8.51 64 6.55c0-9.818-8.578-6.732-13.873-2.745Z"/></svg>';
const X_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>';

const DEFAULT_SOCIAL_LINKS: FooterSocialLink[] = [
  { href: 'https://bsky.app/profile/anomiaapp.bsky.social', label: 'Bluesky', svg: BLUESKY_SVG },
  { href: 'https://x.com/anomia_app', label: 'X (Twitter)', svg: X_SVG },
];

const socialList = (links: readonly FooterSocialLink[]): string =>
  links
    .map(
      (l) =>
        `<a href="${escape(l.href)}" class="footer-social-link" target="_blank" rel="noopener noreferrer" aria-label="${escape(l.label)}">${l.svg}</a>`,
    )
    .join('');

export type FooterProps = {
  /** Override the product-column links. */
  productLinks?: FooterLink[];
  /** Override the recent-posts column. */
  postLinks?: FooterLink[];
  /** Override the contact links. */
  contactLinks?: FooterLink[];
  /** Override the legal-column links (terms / privacy / refunds). */
  legalLinks?: FooterLink[];
  /** Override the social icons in the brand column. */
  socialLinks?: FooterSocialLink[];
};

const DEFAULT_PRODUCT_LINKS: FooterLink[] = [
  { href: '/#caught', label: 'what we catch' },
  { href: '/#pricing', label: 'pricing' },
  { href: '/why', label: 'why this exists' },
];

const recentPostLinks = (): FooterLink[] =>
  [...BLOG_POSTS]
    .sort((a, b) => (a.publishedOn < b.publishedOn ? 1 : -1))
    .slice(0, 3)
    .map((p) => ({ href: `/blog/${p.slug}`, label: p.title }));

const DEFAULT_CONTACT_LINKS: FooterLink[] = [
  { href: 'mailto:contact@anomia.app', label: 'contact@anomia.app' },
];

const DEFAULT_LEGAL_LINKS: FooterLink[] = [
  { href: '/terms', label: 'terms' },
  { href: '/privacy', label: 'privacy' },
  { href: '/refunds', label: 'refunds' },
];

const linkList = (links: readonly FooterLink[]): string =>
  links.map((l) => `<li><a href="${escape(l.href)}">${escape(l.label)}</a></li>`).join('');

export const Footer = (props: FooterProps = {}): HTMLElement => {
  const productLinks = props.productLinks ?? DEFAULT_PRODUCT_LINKS;
  const postLinks = props.postLinks ?? recentPostLinks();
  const contactLinks = props.contactLinks ?? DEFAULT_CONTACT_LINKS;
  const legalLinks = props.legalLinks ?? DEFAULT_LEGAL_LINKS;
  const socialLinks = props.socialLinks ?? DEFAULT_SOCIAL_LINKS;

  return el(`
    <footer>
      <div class="container">
        <div class="footer-grid">
          <div class="footer-brand">
            <div class="wordmark">anomia<span class="wordmark-period">.</span></div>
            <div class="footer-brand-tagline">browser-only PII redactor. your files never leave your device.</div>
            <div class="footer-social" aria-label="social links">${socialList(socialLinks)}</div>
          </div>
          <div class="footer-col">
            <h4>product</h4>
            <ul>${linkList(productLinks)}</ul>
          </div>
          <div class="footer-col">
            <h4>recent posts</h4>
            <ul>${linkList(postLinks)}</ul>
          </div>
          <div class="footer-col">
            <h4>contact</h4>
            <ul>${linkList(contactLinks)}</ul>
          </div>
          <div class="footer-col">
            <h4>legal</h4>
            <ul>${linkList(legalLinks)}</ul>
          </div>
        </div>
      </div>
    </footer>
  `);
};
